import { Box, Button, ChakraProvider, Heading, HStack, Text } from "@chakra-ui/react";
import EsgTheme from "../../theme";
import { colors } from "theme/foundations/colors";
import { BookOpenText, CaretDoubleLeft, CaretRight, Sparkle, X } from "@phosphor-icons/react";
import { useRightSidebar } from "context/RightSidebarContext";

export default function RightSidebarWidget() {
  const { isOpen, doClose, rightSidebar, size, setSize, closable, topOffset, activationId } = useRightSidebar();

  const isAITray = activationId?.startsWith("AI");
  const isKBTray = activationId === "KB";
  const isHeaderTray = isAITray || isKBTray;
  const effectiveTop = 70 + (isHeaderTray ? 0 : (topOffset || 0)) + 2;
  const position = "fixed";

  return (
    <ChakraProvider theme={EsgTheme}>
      <Box
        id="rightSidebarContainer"
        borderLeft="solid 1px"
        borderColor="gray.300"
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
        }}
        style={{
          position: position,
          top: `${effectiveTop}px`,
          right: isOpen ? "0" : "-100%", // Move fully off-screen when closed
          bottom: "0",
          width: { lg: "40vw", md: "30vw", sm: "20vw" }[size] || "30vw",
          overflowY: "auto",
          backgroundColor: colors.sidebar_background,
          padding: "0.1em",
          transition: "right 0.3s ease-in-out", // Slide effect
          display: "flex",
          flexDirection: "column",
          zIndex: 10,
          boxShadow: isOpen ? "0px 4px 10px rgba(0,0,0,0.1)" : "none", // Adds a shadow when open
        }}
      >
        {(doClose || setSize) && (
          <HStack justifyContent="space-between" bg="transparent" px={2} h={isHeaderTray ? "51px" : undefined} flexShrink={0}>
            <HStack spacing={1}>
              {size !== "lg" && (
                <Button onClick={() => setSize("lg")} variant="ghost" size="sm">
                  <CaretDoubleLeft />
                </Button>
              )}
              {size === "lg" && (
                <Button onClick={() => setSize("md")} variant="ghost" size="sm">
                  <CaretRight />
                </Button>
              )}
              {isAITray && (
                <HStack spacing={2}>
                  <Text fontSize="15px" fontWeight="600" color="darkTeal">AI Assistant</Text>
                  <Sparkle size={16} color={colors.navIconLime} />
                </HStack>
              )}
              {isKBTray && (
                <HStack spacing={2}>
                  <Text fontSize="15px" fontWeight="600" color="darkTeal">Knowledge Base</Text>
                  <BookOpenText size={16} color={colors.navIconLime} />
                </HStack>
              )}
            </HStack>
            <HStack spacing={0} ml="auto">
              {doClose && closable && (
                <Button onClick={doClose} variant="ghost" size="sm">
                  <X />
                </Button>
              )}
            </HStack>
          </HStack>
        )}
        <div style={{ padding: "0.8em", flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>{isOpen ? rightSidebar : null}</div>
      </Box>
    </ChakraProvider>
  );
}

/**
 * Sidebar Header Component
 */
export function RightSidebarHeader({ Icon, children }) {
  let weight;
  if (Icon === Sparkle) {
    weight = "fill";
  }
  return (
    <HStack bg="transparent" w="full">
      {Icon && <Icon color={colors.headingLime} weight={weight} size={32} />}
      <Heading as="h2" ml={1} fontSize="heading.l" color="darkTeal" w="full">
        {children}
      </Heading>
    </HStack>
  );
}
